import styled, { createGlobalStyle } from 'styled-components';

export const theme = {
  colors: {
    primary: '#2d5a4e',
    primaryLight: '#3d7a6a',
    primaryDark: '#1e3d35',
    primaryGradient: 'linear-gradient(135deg, #2d5a4e 0%, #1e3d35 100%)',

    secondary: '#4fd1c7',
    secondaryLight: '#81e6d9',
    secondaryDark: '#38b2ac',

    accent: '#ff8a80',
    accentLight: '#ffb3aa',
    accentDark: '#ff6b5f',

    danger: '#ef4444',
    dangerLight: '#f87171',
    dangerDark: '#dc2626',
    error: '#ef4444',

    warning: '#f59e0b',
    success: '#10b981',
    info: '#3b82f6',

    white: '#ffffff',
    offWhite: '#faf9f7',
    gray50: '#f5f4f2',
    gray100: '#ebe9e6',
    gray200: '#d6d4d0',
    gray300: '#b8b5b0',
    gray400: '#9a9790',
    gray500: '#7c7870',
    gray600: '#5e5a52',
    gray700: '#403d36',
    gray800: '#2a2722',
    gray900: '#1a1815',
    black: '#0f0e0c',

    background: '#faf9f7',
    surface: '#ffffff',
    surfaceHover: '#f5f4f2',
    text: '#1a1815',
    textLight: '#403d36',
    textMuted: '#7c7870',
    border: '#d6d4d0',
    borderLight: '#ebe9e6',

    gradient: {
      primary: 'linear-gradient(135deg, #2d5a4e 0%, #1e3d35 100%)',
      secondary: 'linear-gradient(135deg, #4fd1c7 0%, #38b2ac 100%)',
      accent: 'linear-gradient(135deg, #ff8a80 0%, #ff6b5f 100%)',
      warm: 'linear-gradient(135deg, #ff8a80 0%, #ffb3aa 100%)',
      cool: 'linear-gradient(135deg, #4fd1c7 0%, #2d5a4e 100%)',
      dark: 'linear-gradient(135deg, #1a1815 0%, #2a2722 100%)',
      success: 'linear-gradient(135deg, #10b981 0%, #4fd1c7 100%)',
      soft: 'linear-gradient(135deg, #faf9f7 0%, #ebe9e6 100%)',
    }
  },

  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.08), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px rgba(0, 0, 0, 0.12), 0 12px 24px rgba(0, 0, 0, 0.06)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    soft: '0 2px 8px rgba(45, 90, 78, 0.1)',
  },

  borderRadius: {
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.5rem',
    '2xl': '2rem',
    '3xl': '2.5rem',
    full: '9999px',
  },

  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '2.5rem',
    '2xl': '3rem',
    '3xl': '4rem',
    '4xl': '6rem',
  },

  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    xxl: '1.375rem',
    xxxl: '1.625rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },

  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },

  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },

  fontWeights: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },

  transitions: {
    fast: '0.15s ease-in-out',
    normal: '0.2s ease-in-out',
    slow: '0.3s ease-in-out',
  },

  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1200px',
  },
};


export const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Figtree:wght@400;500;600;700;800&display=swap');
  
  :root {
    --color-primary: #006064;
    --color-primary-light: #00838f;
    --color-primary-dark: #004d40;
    --color-secondary: #4fd1c7;
    --color-secondary-light: #81e6d9;
    --color-secondary-dark: #38b2ac;
    --color-accent: #FF8A65;
    --color-accent-light: #ffab91;
    --color-accent-dark: #ff7043;
    --color-danger: #ef4444;
    --color-danger-light: #f87171;
    --color-danger-dark: #dc2626;
    --color-warning: #f59e0b;
    --color-success: #00E676;
    --color-info: #3b82f6;
    --color-background: #121212;
    --color-surface: #1E1E1E;
    --color-surface-hover: #2a2a2a;
    --color-text: #F3F4F6;
    --color-text-light: #e0e0e0;
    --color-text-muted: #9CA3AF;
    --color-border: rgba(255, 255, 255, 0.05);
    --color-border-light: rgba(255, 255, 255, 0.03);
    --color-white: #ffffff;
    --color-off-white: #1E1E1E;
    --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2);
    --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2);
    --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.3);
    --shadow-glow-accent: 0 0 15px rgba(255, 138, 101, 0.3);
  }

  [data-theme="dark"] {
    --color-primary: #006064;
    --color-primary-light: #00838f;
    --color-primary-dark: #004d40;
    --color-secondary: #4fd1c7;
    --color-secondary-light: #81e6d9;
    --color-secondary-dark: #38b2ac;
    --color-accent: #FF8A65;
    --color-accent-light: #ffab91;
    --color-accent-dark: #ff7043;
    --color-danger: #ef4444;
    --color-danger-light: #f87171;
    --color-danger-dark: #dc2626;
    --color-warning: #f59e0b;
    --color-success: #00E676;
    --color-info: #3b82f6;
    --color-background: #121212;
    --color-surface: #1E1E1E;
    --color-surface-hover: #2a2a2a;
    --color-text: #F3F4F6;
    --color-text-light: #e0e0e0;
    --color-text-muted: #9CA3AF;
    --color-border: rgba(255, 255, 255, 0.05);
    --color-border-light: rgba(255, 255, 255, 0.03);
    --color-white: #ffffff;
    --color-off-white: #1E1E1E;
    --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2);
    --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2);
    --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.3);
    --shadow-glow-accent: 0 0 15px rgba(255, 138, 101, 0.3);
  }

  * { 
    margin: 0; 
    padding: 0; 
    box-sizing: border-box; 
  }
  html, body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.6;
    color: #E5E5E5;
    background: #000000;
    background-color: #000000;
    background-attachment: fixed;
    min-height: 100dvh;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-weight: 400;
    transition: background-color 0.3s ease, color 0.3s ease;
  }
  
  * {
    box-shadow: none !important;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-weight: 900;
    letter-spacing: -0.05em;
    text-transform: uppercase;
    color: #ffffff;
  }
  #root { 
    min-height: 100dvh; 
    display: flex; 
    flex-direction: column; 
  }
  @media (max-width: ${theme.breakpoints.tablet}) {
    html, body { 
      min-height: 100dvh; 
      background-attachment: scroll; 
    }
    #root { 
      min-height: 100dvh; 
    }
  }
  a { 
    text-decoration: none; 
    color: inherit; 
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); 
  }
  button {
    cursor: pointer; 
    border: none; 
    outline: none; 
    font-family: inherit;
    font-weight: ${theme.fontWeight.semibold}; 
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0.025em;
  }
  input, select, textarea { 
    font-family: inherit; 
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); 
  }
  .container { 
    max-width: 1400px; 
    margin: 0 auto; 
    padding: 0 2rem; 
  }
  ::-webkit-scrollbar { 
    width: 10px; 
  }
  ::-webkit-scrollbar-track { 
    background: var(--color-border-light); 
    border-radius: 5px;
  }
  ::-webkit-scrollbar-thumb { 
    background: var(--color-border); 
    border-radius: 5px; 
    border: 2px solid var(--color-border-light);
  }
  ::-webkit-scrollbar-thumb:hover { 
    background: var(--color-text-muted); 
  }
  @keyframes fadeIn { 
    from { 
      opacity: 0; 
      transform: translateY(30px); 
    } 
    to { 
      opacity: 1; 
      transform: translateY(0); 
    } 
  }
  @keyframes slideIn { 
    from { 
      opacity: 0; 
      transform: translateX(-30px); 
    } 
    to { 
      opacity: 1; 
      transform: translateX(0); 
    } 
  }
  @keyframes pulse { 
    0%, 100% { 
      opacity: 1; 
    } 
    50% { 
      opacity: 0.6; 
    } 
  }
  @keyframes spin { 
    from { 
      transform: rotate(0deg); 
    } 
    to { 
      transform: rotate(360deg); 
    } 
  }
  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
    }
    50% {
      box-shadow: 0 0 40px rgba(139, 92, 246, 0.8), 0 0 60px rgba(236, 72, 153, 0.4);
    }
  }
  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }
  .animate-fade-in { 
    animation: fadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1); 
  }
  .animate-slide-in { 
    animation: slideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1); 
  }
  .animate-glow {
    animation: glow 3s ease-in-out infinite;
  }
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing['3xl']} ${theme.spacing.md};
  min-height: calc(100dvh - 80px);
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing['3xl']} ${theme.spacing.lg};
  }
`;

export const Card = styled.div<{ variant?: 'default' | 'elevated' | 'glass' }>`
  background: ${({ variant = 'default' }) => {
    if (variant === 'glass') {
      return 'rgba(30, 30, 30, 0.8)';
    }
    return 'var(--color-surface)';
  }};
  backdrop-filter: ${({ variant = 'default' }) => variant === 'glass' ? 'blur(12px)' : 'none'};
  border-radius: ${({ variant = 'default' }) => variant === 'glass' ? theme.borderRadius['3xl'] : theme.borderRadius['2xl']};
  padding: ${theme.spacing['2xl']};
  box-shadow: ${({ variant = 'default' }) =>
    variant === 'elevated' ? 'var(--shadow-lg)' : 'var(--shadow-md)'};
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
  position: relative;
  &:hover { 
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg); 
    border-color: var(--color-border);
  }
  @media (max-width: ${theme.breakpoints.tablet}) { 
    padding: ${theme.spacing.xl}; 
    border-radius: ${theme.borderRadius.xl};
  }
`;

export const Button = styled.button<{
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.fontWeight.semibold};
  transition: all 0.3s ease;
  text-align: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: none;
  font-size: ${theme.fontSize.base};
  font-family: 'Inter', sans-serif;
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
  ${({ loading }) => loading && 'pointer-events: none; opacity: 0.7;'}
  
  &:focus-visible {
    outline: 3px solid ${theme.colors.primary};
    outline-offset: 2px;
    box-shadow: 0 0 0 4px rgba(45, 90, 78, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${({ size = 'md' }) => {
    switch (size) {
      case 'sm':
        return `
          padding: ${theme.spacing.sm} ${theme.spacing.md};
          font-size: ${theme.fontSize.sm};
          border-radius: ${theme.borderRadius.md};
        `;
      case 'lg':
        return `
          padding: ${theme.spacing.lg} ${theme.spacing.xl};
          font-size: ${theme.fontSize.lg};
          border-radius: ${theme.borderRadius.xl};
        `;
      default:
        return `
          padding: ${theme.spacing.md} ${theme.spacing.lg};
          font-size: ${theme.fontSize.base};
        `;
    }
  }}
  ${({ variant = 'primary' }) => {
    switch (variant) {
      case 'secondary':
        return `
          background: linear-gradient(135deg, var(--color-secondary) 0%, var(--color-secondary-dark) 100%);
          color: var(--color-white);
          box-shadow: var(--shadow-sm);
          &:hover { 
            transform: translateY(-2px); 
            box-shadow: var(--shadow-md); 
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: var(--color-primary);
          border: 2px solid var(--color-primary);
          &:hover { 
            background: var(--color-primary); 
            color: var(--color-white); 
            transform: translateY(-2px);
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: var(--color-text-light);
          &:hover { 
            background: var(--color-surface-hover); 
            color: var(--color-text); 
          }
        `;
      case 'danger':
        return `
          background: var(--color-danger);
          color: var(--color-white);
          box-shadow: var(--shadow-sm);
          &:hover { 
            background: var(--color-danger-dark); 
            transform: translateY(-2px); 
            box-shadow: var(--shadow-md); 
          }
        `;
      default:
        return `
          background: var(--color-accent);
          color: var(--color-white);
          box-shadow: var(--shadow-glow-accent);
          font-weight: ${theme.fontWeight.bold};
          &:hover { 
            transform: translateY(-2px); 
            box-shadow: var(--shadow-glow-accent), var(--shadow-md); 
            background: var(--color-accent-dark);
          }
          &:active {
            transform: scale(0.98);
          }
        `;
    }
  }}
  &:disabled { 
    opacity: 0.5; 
    cursor: not-allowed; 
    &:hover { 
      transform: none; 
      box-shadow: ${theme.shadows.soft};
    } 
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: 1px solid var(--color-border);
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSize.base};
  background: var(--color-surface);
  color: var(--color-text);
  transition: all 0.3s ease;
  font-family: 'Roboto', sans-serif;
  &:focus { 
    border-color: var(--color-primary); 
    box-shadow: 0 0 0 3px rgba(0, 96, 100, 0.1); 
    outline: none; 
  }
  &::placeholder { 
    color: var(--color-text-muted); 
  }
  &:disabled { 
    background: var(--color-surface-hover); 
    cursor: not-allowed; 
    opacity: 0.6;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 2px solid var(--color-border);
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSize.base};
  background: var(--color-surface);
  color: var(--color-text);
  transition: all ${theme.transitions.normal};
  cursor: pointer;
  &:focus { border-color: var(--color-primary); box-shadow: 0 0 0 3px rgba(0, 96, 100, 0.2); outline: none; }
  &:disabled { background: var(--color-surface-hover); cursor: not-allowed; }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.sm};
  font-weight: ${theme.fontWeight.medium};
  color: var(--color-text);
  font-size: ${theme.fontSize.sm};
`;

export const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

export const Grid = styled.div<{ columns?: number; gap?: string }>`
  display: grid;
  grid-template-columns: repeat(${({ columns = 1 }) => columns}, 1fr);
  gap: ${({ gap = theme.spacing.lg }) => gap};
  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: 1fr; }
`;

export const Flex = styled.div<{
  direction?: 'row' | 'column';
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
  align?: 'flex-start' | 'center' | 'flex-end' | 'stretch';
  gap?: string;
  wrap?: boolean;
}>`
  display: flex;
  flex-direction: ${({ direction = 'row' }) => direction};
  justify-content: ${({ justify = 'flex-start' }) => justify};
  align-items: ${({ align = 'flex-start' }) => align};
  gap: ${({ gap = '0' }) => gap};
  flex-wrap: ${({ wrap = false }) => wrap ? 'wrap' : 'nowrap'};
`;

export const Title = styled.h1`
  font-size: ${theme.fontSize['4xl']};
  font-weight: ${theme.fontWeight.extrabold};
  color: var(--color-text);
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.2;
  font-family: 'Inter', 'Figtree', sans-serif;
  letter-spacing: -0.02em;
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSize['3xl']};
  }
`;

export const Subtitle = styled.h2`
  font-size: ${theme.fontSize['2xl']};
  font-weight: ${theme.fontWeight.semibold};
  color: var(--color-text);
  margin-bottom: ${theme.spacing.md};
  line-height: 1.3;
  font-family: 'Inter', 'Montserrat', sans-serif;
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSize.xl};
  }
`;

export const Text = styled.p<{ size?: 'xs' | 'sm' | 'base' | 'lg'; color?: string; weight?: string }>`
  font-size: ${({ size = 'base' }) => theme.fontSize[size]};
  color: ${({ color }) => color || 'var(--color-text)'};
  font-weight: ${({ weight = theme.fontWeight.normal }) => weight};
  line-height: 1.6;
`;

export const Badge = styled.span<{ variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' }>`
  display: inline-block;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.025em;
  ${({ variant = 'primary' }) => {
    switch (variant) {
      case 'secondary':
        return `
          background: color-mix(in srgb, var(--color-secondary) 20%, transparent);
          color: var(--color-secondary-light);
        `;
      case 'success':
        return `
          background: color-mix(in srgb, var(--color-success) 20%, transparent);
          color: var(--color-success);
        `;
      case 'warning':
        return `
          background: color-mix(in srgb, var(--color-warning) 20%, transparent);
          color: var(--color-accent-light);
        `;
      case 'danger':
        return `
          background: color-mix(in srgb, var(--color-danger) 20%, transparent);
          color: var(--color-danger-light);
        `;
      default:
        return `
          background: color-mix(in srgb, var(--color-primary) 20%, transparent);
          color: var(--color-primary-light);
        `;
    }
  }}
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

export const ErrorMessage = styled.div`
  color: var(--color-danger-light);
  font-size: ${theme.fontSize.sm};
  margin-top: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;
